/**
 * AuthErrorFragmentCatcher — app-wide safety net for auth email returns that
 * land OUTSIDE the auth routes.
 *
 * Two canonical cases, both fail-safe (never a silent homepage):
 *
 * 1. REJECTED LINK — GoTrue appended `#error=…` to whatever route it fell back
 *    to (often `/`). We route into the dedicated recovery screen instead of
 *    rendering marketing content, preserving the browser's pending-scan
 *    identity (`scan_request_id` + email) so recovery can rebind the same
 *    scan. The original URL fragment is stripped so the raw error never
 *    flashes again.
 *
 * 2. SUCCESSFUL SIGN-IN WITH A PENDING SCAN — a verification email whose
 *    redirect was lost falls back to the Site URL: the session establishes on
 *    `/` and the user would otherwise land on the marketing homepage mid-
 *    funnel. On the SIGNED_IN event we consult the browser-local pending-scan
 *    handle; only when the signed-in email MATCHES the pending scan's email
 *    do we route into `/auth/callback?scan_request_id=S` so the canonical
 *    callback resumes the scan. Identity is never guessed from ambient state.
 *
 * Mounted once inside the router (see PublicLayout / AppLayout).
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// The backend client is loaded dynamically inside the effect below so
// @supabase/supabase-js stays out of the first-paint bundle.
import { parseAuthLinkError } from "@/lib/auth-link-error";
import { readPendingScan } from "@/lib/pending-scan-store";
import { AUTH_RECOVERY_PATH, resolveAuthReturnRoute } from "@/lib/auth-return-routing";

const HANDLED_PATHS = new Set([AUTH_RECOVERY_PATH, "/auth/reset-password"]);

export function AuthErrorFragmentCatcher() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Case 1: rejected-link error fragment ────────────────────────────────
  useEffect(() => {
    if (HANDLED_PATHS.has(location.pathname)) return;
    const linkError = parseAuthLinkError({
      hash: location.hash,
      search: location.search,
    });
    if (!linkError?.recoverable) return;

    const persisted = readPendingScan();
    const params = new URLSearchParams();
    if (linkError.code) params.set("error_code", linkError.code);
    if (linkError.description) params.set("error_description", linkError.description);
    const scanRequestId =
      new URLSearchParams(location.search).get("scan_request_id") ??
      persisted?.scan_request_id ??
      null;
    if (scanRequestId) params.set("scan_request_id", scanRequestId);
    if (persisted?.email) params.set("email", persisted.email);

    // Strip the raw error fragment before leaving the route.
    if (location.hash.includes("error=")) {
      window.history.replaceState(null, "", location.pathname + location.search);
    }
    navigate(`${AUTH_RECOVERY_PATH}?${params.toString()}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  // ── Case 2: fresh sign-in outside auth routes with a pending scan ───────
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void import("@/integrations/supabase/client").then(({ supabase }) => {
      if (cancelled) return;
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        if (event !== "SIGNED_IN") return;
        const decision = resolveAuthReturnRoute({
          pathname: window.location.pathname,
          pendingScan: readPendingScan(),
          signedInEmail: session?.user?.email ?? null,
        });
        if (decision.type === "resume_scan") {
          navigate(decision.to, { replace: true });
        }
      });
      unsubscribe = () => subscription.unsubscribe();
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [navigate]);

  return null;
}

export default AuthErrorFragmentCatcher;
